import { findRoleTrack } from './data/roleKeywords';
import { findPhrases, ratio } from './text';
import type { KeywordAnalysis, ProfileInput } from './types';

/**
 * Every piece of profile text a keyword could legitimately appear in.
 *
 * Skills are included because LinkedIn's own search indexes them, so a keyword
 * present only as a skill still counts as covered.
 */
export function searchableText(profile: ProfileInput): string {
  return [
    profile.headline,
    profile.about,
    ...profile.experience.flatMap((entry) => [entry.title, entry.organisation, entry.description]),
    ...profile.skills,
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Measures how well the profile covers its target role's expected vocabulary.
 *
 * Only `must` keywords count toward `coverage`; `nice` keywords are surfaced as
 * opportunities in `missing` but never drag the score down. When the user has
 * not picked a target role we return a null analysis, and the rules that depend
 * on it mark themselves skipped rather than scoring zero.
 */
export function analyzeKeywords(profile: ProfileInput): KeywordAnalysis {
  const track = findRoleTrack(profile.targetRole);

  if (!track) {
    return { roleId: null, roleLabel: null, coverage: 0, present: [], missing: [] };
  }

  const haystack = searchableText(profile);
  const present = findPhrases(haystack, track.must);
  const missing = track.must.filter((keyword) => !present.includes(keyword));

  // Bonus keywords are reported alongside the gaps so the user sees the upside,
  // but they are deliberately excluded from the coverage denominator.
  const bonusPresent = findPhrases(haystack, track.nice);

  return {
    roleId: track.id,
    roleLabel: track.label,
    coverage: ratio(present.length, track.must.length),
    present: [...present, ...bonusPresent],
    missing,
  };
}
